import { FC, useMemo } from 'react';
import cn from 'classnames';
import { Card } from '../../../../../../components/card/card';
import { Tooltip } from '../../../../../../features/tooltip/tooltip';
import { useLang } from '../../../../../../hooks/use-lang';
import { ExternalMonitoringFiltersState } from '../dashboard-external-monitoring-filters';
import type { ExternalMonitoringKpiPayload } from '../external-monitoring-aggregate';
import s from './dashboard-external-monitoring-kpis.module.css';

type KpiCategoryHighlight = {
  id: string;
  name: string;
  positive: number;
  neutral: number;
  negative: number;
};

type Props = {
  filters: ExternalMonitoringFiltersState;
  onFiltersChange: (patch: Partial<ExternalMonitoringFiltersState>) => void;
  kpiPayload?: ExternalMonitoringKpiPayload | null;
  /** Позитив − негатив по периодам (те же бакеты, что «Динамика тональности»). */
  toneNetSparklineValues?: number[] | null;
};

function clampPercent(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function formatPercent(n: number): string {
  const v = Math.round(n);
  return `${v}%`;
}

function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('ru-RU').format(n);
}

function buildSparklinePoints(values: number[], width: number, height: number): string {
  const clean = values.map((v) => (Number.isFinite(v) ? v : 0));
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const span = Math.max(1e-6, max - min);
  const stepX = clean.length > 1 ? width / (clean.length - 1) : width;
  return clean
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / span) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

function polarToCartesian(cx: number, cy: number, r: number, angleRad: number): { x: number; y: number } {
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
}

function IconChartLine() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 17l6-6 4 4 6-8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSmile() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M8.5 10h.01M15.5 10h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M8.5 14.5c1.2 1.2 2.5 1.8 3.5 1.8s2.3-.6 3.5-1.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconMeh() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M8.5 10h.01M15.5 10h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8.5 15.2h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconSad() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M8.5 10h.01M15.5 10h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M8.5 16.2c1.2-1.2 2.5-1.8 3.5-1.8s2.3.6 3.5 1.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconStar() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 17.3l-5.2 3 1-5.9-4.3-4.2 6-0.9L12 4l2.5 5.3 6 0.9-4.3 4.2 1 5.9-5.2-3z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconAlert() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 9v4m0 4h.01M10.3 4.8l-8 14A2 2 0 004 22h16a2 2 0 001.7-3.2l-8-14a2 2 0 00-3.4 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const DashboardExternalMonitoringKpis: FC<Props> = ({
  filters,
  onFiltersChange,
  kpiPayload,
  toneNetSparklineValues,
}) => {
  const { t } = useLang();
  const kpiModeLabel = filters.categoryId === 'all' ? t('Категория') : t('Аспект');
  const kpiTooltips = {
    totalComments: t(
      'Общее количество собранных текстовых отзывов из выбранных источников за указанный период. Включает расчет общего числа выявленных смысловых аспектов.',
    ),
    sentimentIndex: t(
      'Сводный показатель лояльности. Рассчитывается как разница между процентной долей положительных и отрицательных комментариев. Диапазон: от -100 до +100.',
    ),
    overallSentiment: t(
      'Распределение комментариев по типу эмоциональной окраски: положительная, нейтральная или отрицательная. Базируется на итоговой оценке текста.',
    ),
    driver: t(
      'Категория с максимальной долей положительных оценок относительно общего объема упоминаний в данном сегменте.',
    ),
    risk: t(
      'Категория, имеющая наибольший объем отрицательных упоминаний и оказывающая максимальное негативное влияние на общий индекс настроения.',
    ),
  };
  const renderKpiTooltip = (content: string) => (
    <Tooltip
      behavior="hover"
      position="top"
      offset={10}
      customTrigger={document.body}
      trigger={
        <span className={s.kpiHintBtn} tabIndex={0} role="img" aria-label={t('Справка')}>
          !
        </span>
      }
      onClickTrigger={(e) => e.stopPropagation()}
      optionsWrapperClassName={s.kpiTooltipPanel}
    >
      <div>{content}</div>
    </Tooltip>
  );
  const kpis = useMemo(() => {
    const sparklineValues = (() => {
      if (toneNetSparklineValues?.length) {
        const v = toneNetSparklineValues;
        return v.length === 1 ? [v[0], v[0]] : [...v];
      }
      if (kpiPayload) {
        return [kpiPayload.totalComments, kpiPayload.totalComments];
      }
      return [0, 0];
    })();

    if (kpiPayload) {
      const posTotal = kpiPayload.positive;
      const neuTotal = kpiPayload.neutral;
      const negTotal = kpiPayload.negative;
      const sentimentSum = posTotal + neuTotal + negTotal;

      const sentimentIndex = kpiPayload.sentimentIndex;
      const indexColor =
        sentimentIndex > 20 ? '#12B76A' : sentimentIndex < -20 ? '#F04438' : '#FEC84B';

      const posPct =
        sentimentSum > 0 ? clampPercent((posTotal / sentimentSum) * 100) : 0;
      const neuPct =
        sentimentSum > 0 ? clampPercent((neuTotal / sentimentSum) * 100) : 0;
      const negPct =
        sentimentSum > 0 ? clampPercent((negTotal / sentimentSum) * 100) : 0;

      const driver: KpiCategoryHighlight | null = kpiPayload.driver
        ? {
            id: kpiPayload.driver.id,
            name: kpiPayload.driver.name,
            positive: kpiPayload.driver.positive,
            neutral: kpiPayload.driver.neutral,
            negative: kpiPayload.driver.negative,
          }
        : null;
      const risk: KpiCategoryHighlight | null = kpiPayload.risk
        ? {
            id: kpiPayload.risk.id,
            name: kpiPayload.risk.name,
            positive: kpiPayload.risk.positive,
            neutral: kpiPayload.risk.neutral,
            negative: kpiPayload.risk.negative,
          }
        : null;

      return {
        total: kpiPayload.totalComments,
        aspectsCount: kpiPayload.aspectsCount,
        sentimentIndex,
        indexColor,
        posPct,
        neuPct,
        negPct,
        driver,
        risk,
        sparklineValues,
      };
    }

    return {
      total: 0,
      aspectsCount: 0,
      sentimentIndex: 0,
      indexColor: '#FEC84B' as const,
      posPct: 0,
      neuPct: 0,
      negPct: 0,
      driver: null,
      risk: null,
      sparklineValues,
    };
  }, [kpiPayload, toneNetSparklineValues]);

  /** Глобальные метрики из type=sentiment без разреза по категории — при фильтре категории блоки скрываем */
  const hideGlobalSentimentKpis = filters.categoryId !== 'all';
  const noData = kpis.total <= 0;
  /** При срезе по категории драйвер/риск из аспектов — не глушим из‑за пустого глобального sentiment */
  const hasDriver = Boolean(kpis.driver) && (hideGlobalSentimentKpis || !noData);
  const hasRisk = Boolean(kpis.risk) && (hideGlobalSentimentKpis || !noData);
  const showDriverRisk = hasDriver || hasRisk;
  const sentimentIcon =
    kpis.sentimentIndex > 20 ? <IconSmile /> : kpis.sentimentIndex < -20 ? <IconSad /> : <IconMeh />;

  if (hideGlobalSentimentKpis && !showDriverRisk) {
    return null;
  }

  return (
    <div
      className={cn(
        s.row,
        hideGlobalSentimentKpis && showDriverRisk && s.rowGlobalKpisHidden,
        !hideGlobalSentimentKpis && !showDriverRisk && s.rowWithoutDriverRisk,
      )}
    >
      {!hideGlobalSentimentKpis && (
        <Card
          className={s.card}
          onClick={() => {
            if (noData) return;
            onFiltersChange({
              sentimentKey: 'all',
              categoryId: 'all',
              // aspectId: 'all',
              sourceId: 'all',
            });
          }}
        >
        <div className={s.titleRow}>
          <div className={s.titleWithHint}>
            <div className={s.title}>{t('Всего комментариев')}</div>
            {renderKpiTooltip(kpiTooltips.totalComments)}
          </div>
          <span className={cn(s.iconBubble, s.iconBubbleGreen)} aria-hidden>
            <IconChartLine />
          </span>
        </div>
        <div className={s.value}>{noData ? '—' : formatCompact(kpis.total)}</div>
        <div className={s.subValue}>
          {noData ? '—' : t('Выявлено {{count}} аспектов', { count: kpis.aspectsCount })}
        </div>
        {!noData && (
          <svg className={s.sparkline} viewBox="0 0 120 34" preserveAspectRatio="none" aria-hidden>
            {(() => {
              const daily = kpis.sparklineValues;
              const pts = buildSparklinePoints(daily, 120, 34);
              const area = `${pts} 120,34 0,34`;
              return (
                <>
                  <polygon className={s.sparklineArea} points={area} />
                  <polyline className={s.sparklinePath} points={pts} />
                </>
              );
            })()}
          </svg>
        )}
        </Card>
      )}

      {!hideGlobalSentimentKpis && (
        <Card
          className={s.card}
          onClick={() => {
            if (noData) return;
            onFiltersChange({ sentimentKey: 'posNeg' });
          }}
        >
        <div className={s.titleRow}>
          <div className={s.titleWithHint}>
            <div className={s.title}>{t('Индекс настроения')}</div>
            {renderKpiTooltip(kpiTooltips.sentimentIndex)}
          </div>
          <span
            className={cn(
              s.iconBubble,
              kpis.sentimentIndex > 20
                ? s.iconBubbleGreen
                : kpis.sentimentIndex < -20
                  ? s.iconBubbleRed
                  : s.iconBubbleYellow,
            )}
            aria-hidden
          >
            {sentimentIcon}
          </span>
        </div>
        <div className={s.value}>{noData ? '—' : `${Math.round(kpis.sentimentIndex)}`}</div>
        <div className={s.gauge} aria-hidden>
          <div className={s.gaugeTrack}>
            <div
              className={s.gaugeFill}
              style={{ width: `${clampPercent(((kpis.sentimentIndex + 100) / 200) * 100)}%` }}
            />
          </div>
          <div className={s.gaugeTicks}>
            <span>-100</span>
            <span>0</span>
            <span>100</span>
          </div>
        </div>
        </Card>
      )}

      {!hideGlobalSentimentKpis && (
        <Card className={cn(s.card, s.cardPassive)}>
        <div className={s.titleRow}>
          <div className={s.titleWithHint}>
            <div className={s.title}>{t('Общая тональность')}</div>
            {renderKpiTooltip(kpiTooltips.overallSentiment)}
          </div>
        </div>
        <div className={s.donutWrap}>
          {noData ? (
            <div className={s.value}>—</div>
          ) : (
            (() => {
              const r = 38;
              const c = 2 * Math.PI * r;
              const pos = (kpis.posPct / 100) * c;
              const neu = (kpis.neuPct / 100) * c;
              const neg = (kpis.negPct / 100) * c;
              const gap = 2.5;
              const start = 0;
              const posDash = `${Math.max(0, pos - gap)} ${c}`;
              const neuDash = `${Math.max(0, neu - gap)} ${c}`;
              const negDash = `${Math.max(0, neg - gap)} ${c}`;
              const posOff = start;
              const neuOff = start - pos;
              const negOff = start - pos - neu;

              return (
                <svg className={s.donutOnly} viewBox="0 0 120 120" aria-hidden>
                  <circle cx="60" cy="60" r={r} stroke="#EAECF0" strokeWidth="20" fill="none" />
                  <circle
                    cx="60"
                    cy="60"
                    r={r}
                    stroke="#12B76A"
                    strokeWidth="20"
                    fill="none"
                    strokeDasharray={posDash}
                    strokeDashoffset={posOff}
                    transform="rotate(-90 60 60)"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r={r}
                    stroke="#98A2B3"
                    strokeWidth="20"
                    fill="none"
                    strokeDasharray={neuDash}
                    strokeDashoffset={neuOff}
                    transform="rotate(-90 60 60)"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r={r}
                    stroke="#F04438"
                    strokeWidth="20"
                    fill="none"
                    strokeDasharray={negDash}
                    strokeDashoffset={negOff}
                    transform="rotate(-90 60 60)"
                    strokeLinecap="round"
                  />
                  {(() => {
                    // подписи процентов по центру каждого сегмента
                    const segments = [
                      { pct: kpis.posPct, label: formatPercent(kpis.posPct) },
                      { pct: kpis.neuPct, label: formatPercent(kpis.neuPct) },
                      { pct: kpis.negPct, label: formatPercent(kpis.negPct) },
                    ];
                    const startAngle = -Math.PI / 2; // сверху
                    let angle = startAngle;
                    const labelR = r; // середина кольца (stroke центрируется на r)
                    return segments.map((seg, idx) => {
                      const segAngle = (seg.pct / 100) * Math.PI * 2;
                      const mid = angle + segAngle / 2;
                      angle += segAngle;
                      // если сегмент почти нулевой — не рисуем подпись
                      if (seg.pct < 6) return null;
                      const { x, y } = polarToCartesian(60, 60, labelR, mid);
                      return (
                        <text
                          key={idx}
                          x={x}
                          y={y + 4}
                          textAnchor="middle"
                          className={s.donutPct}
                        >
                          {seg.label}
                        </text>
                      );
                    });
                  })()}
                </svg>
              );
            })()
          )}
        </div>
        </Card>
      )}

      {showDriverRisk ? (
        <>
          {/*
           * Ранее при categoryId !== 'all': onFiltersChange({ aspectId: kpis.driver.id }) — провал по аспекту.
           * Сейчас карточка пассивна при выбранной категории; чтобы вернуть — раскомментируйте блок ниже и уберите cardPassive/onClick-ограничение.
           *
          <Card
            className={s.card}
            onClick={() => {
              if (noData || !kpis.driver?.id) return;
              if (filters.categoryId === 'all') {
                onFiltersChange({ categoryId: kpis.driver.id, aspectId: 'all' });
                return;
              }
              onFiltersChange({ aspectId: kpis.driver.id });
            }}
          >
          */}
          {hasDriver && (
            <Card
              className={cn(s.card, filters.categoryId !== 'all' && s.cardPassive)}
              onClick={() => {
                if (noData || !kpis.driver?.id) return;
                if (filters.categoryId !== 'all') return;
                onFiltersChange({ categoryId: kpis.driver.id });
              }}
            >
              <div className={s.titleRow}>
                <div className={s.titleWithBadge}>
                  <span className={s.titleWithHint}>
                    <span className={s.title}>{t('Сильная сторона')}</span>
                    {renderKpiTooltip(kpiTooltips.driver)}
                  </span>
                  <span className={s.modeBadge}>{kpiModeLabel}</span>
                </div>
                <span className={cn(s.iconBubble, s.iconBubbleGreen)} aria-hidden>
                  <IconStar />
                </span>
              </div>
              <div className={s.value}>{noData ? '—' : (kpis.driver?.name ?? '—')}</div>
              <div className={s.subValue}>
                {noData || !kpis.driver
                  ? '—'
                  : t('Позитивных: {{formatted}}', { formatted: formatCompact(kpis.driver.positive) })}
              </div>
            </Card>
          )}

          {/*
          <Card
            className={s.card}
            onClick={() => {
              if (noData || !kpis.risk?.id) return;
              if (filters.categoryId === 'all') {
                onFiltersChange({ categoryId: kpis.risk.id, aspectId: 'all' });
                return;
              }
              onFiltersChange({ aspectId: kpis.risk.id });
            }}
          >
          */}
          {hasRisk && (
            <Card
              className={cn(s.card, filters.categoryId !== 'all' && s.cardPassive)}
              onClick={() => {
                if (noData || !kpis.risk?.id) return;
                if (filters.categoryId !== 'all') return;
                onFiltersChange({ categoryId: kpis.risk.id });
              }}
            >
              <div className={s.titleRow}>
                <div className={s.titleWithBadge}>
                  <span className={s.titleWithHint}>
                    <span className={s.title}>{t('Зона риска')}</span>
                    {renderKpiTooltip(kpiTooltips.risk)}
                  </span>
                  <span className={s.modeBadge}>{kpiModeLabel}</span>
                </div>
                <span className={cn(s.iconBubble, s.iconBubbleRed)} aria-hidden>
                  <IconAlert />
                </span>
              </div>
              <div className={s.value}>{noData ? '—' : (kpis.risk?.name ?? '—')}</div>
              <div className={s.subValue}>
                {noData || !kpis.risk
                  ? '—'
                  : t('Негативных: {{formatted}}', { formatted: formatCompact(kpis.risk.negative) })}
              </div>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
};

